import {
    Component,
    Input,
    Output,
    ElementRef,
    OnInit,
    OnDestroy,
    OnChanges,
    AfterContentInit,
    EventEmitter,
    SimpleChanges
} from '@angular/core';
import { throttle, isNumber } from 'lodash';
import { CognitiveAssessment } from '../../core-data-model/CognitiveAssessment.model';
import { DateFormatUtility } from '../../core-utilities/DateFormat.utility';
import { SVGUtilities } from '../../core-utilities/SVG.utility';
import { Util } from '../../core-utilities/Util';
import { TranslationService } from '../../translations';

type Placement = {
    textAnchor: string;
    textStart: number;
    lineHeight: number;
    isLow: boolean;
};

@Component({
    selector: 'cognitive-assessment-image',
    template: `
        <div
            maskInLogRocket
            class="scalingSvgContainer"
            piTesting="cognitive-assessment-image-component"
            [ngStyle]="{ 'padding-bottom': getPaddingBottom() }"
        >
            <svg
                class="scalingSvg"
                xmlns="http://www.w3.org/2000/svg"
                xmlns:xlink="http://www.w3.org/1999/xlink"
                [attr.viewBox]="getViewboxDimensions()"
            >
                <svg:defs>
                    <svg:linearGradient
                        id="linearGradient-cog-person"
                        gradientUnits="userSpaceOnUse"
                    >
                        <svg:stop offset="0" stop-color="#b6e4fa" />
                        <svg:stop offset="1" stop-color="#0092c8" />
                    </svg:linearGradient>
                    <svg:linearGradient
                        id="linearGradient-cog-job"
                        gradientUnits="userSpaceOnUse"
                    >
                        <svg:stop offset="0" stop-color="#fcc18c" />
                        <svg:stop offset="1" stop-color="#f47641" />
                    </svg:linearGradient>
                </svg:defs>
                <svg:g transform="translate(0, 4)">
                    <!-- white background -->
                    <svg:rect
                        x="0"
                        y="0"
                        [attr.width]="jobBackgroundLocation()"
                        [attr.height]="getViewboxHeight()"
                        style="fill: white; display: none;"
                    ></svg:rect>
                    <svg:g transform="translate(1, -4) scale(1.085, 1)">
                        <!-- left trailing graph line -->
                        <svg:path
                            d="M475,208.8H449.33l4.52.59c1,.13,2,.2,2.94.28l2,.17c3.26.36,6.57.51,10.16.67,2,.09,4.06.09,6,.09Z"
                            style="fill:#c9cbcc"
                        />
                        <!-- right trailing graph line -->
                        <svg:path
                            d="M30.09,208.85H29v1.76h1c5.72,0,11.51-.28,17.22-.81,2.73-.25,5.46-.58,8.19-1H30.09Z"
                            style="fill:#c9cbcc"
                        />
                        <!-- graph line -->
                        <svg:path
                            d="M469,208.7c-3.59-.15-6.81-.31-10.11-.67l-2-.17c-1-.08-1.92-.16-2.89-.28-2.82-.34-6-.75-9.1-1.24-3.33-.54-6.65-1.15-9.91-1.83-2.83-.59-5.74-1.27-8.5-2-4.76-1.28-9.54-2.75-14.18-4.39-5.29-1.88-10.56-4-15.67-6.39-4-1.88-8-3.9-11.89-6-4.6-2.53-9.17-5.29-13.58-8.2-3.5-2.3-6.5-4.4-9.26-6.39-4.32-3.19-8.64-6.53-12.78-9.94-2.89-2.35-5.84-4.79-8.6-7.26-3.35-2.91-6.74-5.92-10.08-9L327.35,142l-3.57-3.33-8.31-8.1c-1.06-1-2.13-2-3.19-3l-3.12-2.91c-3.88-3.67-7.9-7.24-12-10.65-2.09-1.74-4.24-3.47-6.39-5.12a95.43,95.43,0,0,0-10-6.8,63.78,63.78,0,0,0-14.62-6.66,44.52,44.52,0,0,0-8.9-1.71,46.87,46.87,0,0,0-6.2-.12,56.75,56.75,0,0,0-8.09,1,60.62,60.62,0,0,0-13,4.23,83.31,83.31,0,0,0-8.28,4.29c-2.9,1.71-5.81,3.61-8.57,5.63-2.08,1.56-4.17,3.11-6.12,4.66-2.86,2.28-5.62,4.57-8.38,7l-1.2,1c-1.71,1.51-3.35,2.94-5.11,4.52s-3.49,3.25-5.23,4.81l-5.52,5.13-4,3.65q-4.4,4.12-8.86,8.13l-4.14,3.67c-2.89,2.61-6,5.35-9.94,8.61-1.27,1.12-2.65,2.19-4,3.28l-2.76,2.18c-4.09,3.33-8.24,6.3-11.76,8.77-2.17,1.54-4.38,3-6.59,4.46-3.8,2.52-7.74,4.9-11.64,7.09s-8,4.34-12.18,6.36a173.45,173.45,0,0,1-19.33,7.77,171.65,171.65,0,0,1-19.18,5.19,178,178,0,0,1-19.92,3c-5.69.51-11.44.81-17.12.81H55.44c4-.55,7.91-1.21,11.83-2A173.26,173.26,0,0,0,86.6,201.7a174.09,174.09,0,0,0,19.45-7.78c4.17-2,8.31-4.13,12.23-6.39s7.9-4.67,11.64-7.11c2.21-1.46,4.43-2.92,6.61-4.48,3.55-2.49,7.69-5.48,11.83-8.82l2.76-2.18c1.3-1.09,2.68-2.18,4.06-3.3,3.91-3.27,7.08-6,10-8.65l4.14-3.67q4.47-4,8.88-8.15l3.95-3.64,5.52-5.13c1.74-1.62,3.53-3.28,5.22-4.8s3.38-3,5.09-4.51l1.19-1c2.66-2.36,5.52-4.68,8.28-6.9,1.93-1.46,4-3.13,6.07-4.57A89.19,89.19,0,0,1,222,105c2.87-1.62,5.63-3,8.28-4.24a59.56,59.56,0,0,1,12.83-4.17,56,56,0,0,1,8-.95,45.2,45.2,0,0,1,6.09.12,43.47,43.47,0,0,1,8.71,1.55A62.93,62.93,0,0,1,280.26,104a93.69,93.69,0,0,1,9.93,6.72c2.14,1.63,4.28,3.34,6.35,5.08,4,3.38,8,7,11.89,10.59l3.13,2.91,3.18,3,8.35,7.95,3.59,3.34,3.05,2.83c3.36,3.09,6.75,6.14,10.11,9.06,2.83,2.49,5.74,4.93,8.63,7.29,4.18,3.42,8.51,6.76,12.84,10,2.75,2,5.79,4.11,9.3,6.42,4.45,2.92,9,5.69,13.67,8.24,3.91,2.13,7.92,4.15,11.94,6,5.14,2.36,10.44,4.54,15.77,6.44,4.67,1.65,9.46,3.12,14.25,4.4,2.83.76,5.7,1.45,8.55,2.05,3.27.68,6.61,1.3,10,1.83,1.52.24,3.08.47,4.6.67H475C473,208.8,471,208.8,469,208.7Z"
                            style="fill:#c9cbcc"
                        />
                    </svg:g>

                    <!-- Score marker stuff -->
                    <svg:g transform="translate(0, 14)">
                        <!-- score line (job) -->
                        @if (hasJobScore()) {
                            <svg:g
                                [attr.transform]="
                                    SVG.outputTranslate(
                                        lineLocation(jobScore),
                                        0
                                    )
                                "
                            >
                                <svg:line
                                    x1="0"
                                    x2="0"
                                    y1="192.29"
                                    [attr.y2]="
                                        getDisplayParameters(true).lineHeight
                                    "
                                    style="fill: none;stroke:#231f20;stroke-miterlimit: 10;stroke-width:1.67634260995414px"
                                />
                                @if (draggable) {
                                    <svg:g
                                        [attr.transform]="
                                            SVG.outputTranslate(
                                                0,
                                                getMiddleOfScoreLine()
                                            )
                                        "
                                    >
                                        <svg:polygon
                                            fill="#f47642"
                                            [attr.points]="
                                                dragTrianglePoints[0]
                                            "
                                        />
                                        <svg:polygon
                                            fill="#f47642"
                                            [attr.points]="
                                                dragTrianglePoints[1]
                                            "
                                        />
                                    </svg:g>
                                }
                                @if (draggable) {
                                    <svg:rect
                                        [attr.x]="(dragRectWidth * -1) / 2"
                                        [attr.y]="
                                            getDisplayParameters(true)
                                                .lineHeight
                                        "
                                        [attr.width]="dragRectWidth"
                                        [attr.height]="
                                            192.29 -
                                            getDisplayParameters(true)
                                                .lineHeight
                                        "
                                        [ngStyle]="draggableRectStyle"
                                        (mousedown)="dragMousedown()"
                                        piTesting="cognitive-assessment-image-draggable-rect"
                                    />
                                }
                            </svg:g>
                        }
                        <!-- score line (person) -->
                        @if (personScore !== undefined) {
                            <svg:g
                                [attr.transform]="
                                    SVG.outputTranslate(
                                        lineLocation(personScore),
                                        0
                                    )
                                "
                            >
                                <svg:line
                                    x1="0"
                                    y1="192.29"
                                    x2="0"
                                    [attr.y2]="
                                        getDisplayParameters(false).lineHeight
                                    "
                                    style="fill: none;stroke:#231f20;stroke-miterlimit: 10;stroke-width:1.67634260995414px"
                                />
                            </svg:g>
                        }

                        <!-- score marker (job) -->
                        @if (hasJobScore()) {
                            <svg:g
                                [attr.transform]="
                                    SVG.outputTranslate(
                                        lineLocation(jobScore),
                                        getDisplayParameters(true).lineHeight
                                    )
                                "
                            >
                                @if (getDisplayParameters(true).isLow) {
                                    <svg:rect
                                        height="26"
                                        style="fill:white"
                                        width="37"
                                        x="-18"
                                        y="-26"
                                    ></svg:rect>
                                }
                                @if (getDisplayParameters(true).isLow) {
                                    <svg:rect
                                        height="26"
                                        style="fill:white"
                                        width="14"
                                        x="-7"
                                        y="-29"
                                    ></svg:rect>
                                }
                                <svg:path
                                    d="M18.55,-22.44v7.49h-35V-22.44a3.08,3.08,0,0,1,3.08-3.08h6.88V-28.6a1.87,1.87,0,0,1,1.85-1.88h11.29a1.87,1.87,0,0,1,1.88,1.85v3.11h6.88a3.08,3.08,0,0,1,3.08,3.08Zm0,9.37v9.37a3.08,3.08,0,0,1-3.08,3.08H-13.27a3.08,3.08,0,0,1-3.08-3.08V-13.07h13.13v3.08a1.26,1.26,0,0,0,1.25,1.25h6.25a1.26,1.26,0,0,0,1.25-1.25V-13.07ZM-4.01,-25.57h10V-28.11h-10Zm7.5,12.5v2.5h-5v-2.5Z"
                                    style="fill:#f47642"
                                />
                                <svg:g
                                    [attr.transform]="
                                        SVG.outputTranslate(
                                            getDisplayParameters(true)
                                                .textStart,
                                            0
                                        )
                                    "
                                >
                                    <svg:text
                                        transform="translate(0 -20.35)"
                                        textLength="48"
                                        lengthAdjust="spacingAndGlyphs"
                                        [attr.text-anchor]="
                                            getDisplayParameters(true)
                                                .textAnchor
                                        "
                                        style="text-transform: uppercase; font-size:14.23013973236084px;fill:#f47642;font-family:Roboto-Bold, Roboto;font-weight: 700;letter-spacing:0.07497428354998997em"
                                    >
                                        {{ TR.key('text.common-terms.score') }}
                                    </svg:text>
                                    <svg:text
                                        transform="translate(0 5)"
                                        textLength="48"
                                        [attr.text-anchor]="
                                            getDisplayParameters(true)
                                                .textAnchor
                                        "
                                        style="font-size:29.482959747314453px;fill:#f47642;font-family:Roboto-Bold, Roboto;font-weight:700"
                                    >
                                        {{ jobScore }}
                                    </svg:text>
                                </svg:g>
                            </svg:g>
                        }
                        <!-- score marker (person) -->
                        @if (hasPersonScore()) {
                            <svg:g
                                [attr.transform]="
                                    SVG.outputTranslate(
                                        lineLocation(personScore),
                                        getDisplayParameters(false).lineHeight
                                    )
                                "
                            >
                                @if (getDisplayParameters(false).isLow) {
                                    <svg:rect
                                        height="33"
                                        style="fill:white"
                                        width="16"
                                        x="-8"
                                        y="-31"
                                    ></svg:rect>
                                }
                                <svg:path
                                    d="M12.8,-4.96a5.8,5.8,0,0,1-1.25,3.74,3.75,3.75,0,0,1-3,1.56H-8.55a3.74,3.74,0,0,1-3-1.56A5.79,5.79,0,0,1,-12.81,-4.96a28.66,28.66,0,0,1,.17-3.21,17.22,17.22,0,0,1,.63-3A10.13,10.13,0,0,1,-10.81,-13.38a5.6,5.6,0,0,1,1.88-1.78,5.19,5.19,0,0,1,2.69-.69,8.93,8.93,0,0,0,12.52,0,5.19,5.19,0,0,1,2.69.69A5.58,5.58,0,0,1,10.82,-13.38a10.13,10.13,0,0,1,1.17,2.62,17.21,17.21,0,0,1,.63,3A28.65,28.65,0,0,1,12.8,-4.96Zm-7.37-23.17a7.68,7.68,0,1,1-5.43-2.25A7.4,7.4,0,0,1,5.43,-27.71Z"
                                    style="fill:#0092c8"
                                />
                                <!-- score number -->
                                <svg:g
                                    [attr.transform]="
                                        SVG.outputTranslate(
                                            getDisplayParameters(false)
                                                .textStart,
                                            0
                                        )
                                    "
                                >
                                    <svg:text
                                        [attr.transform]="
                                            SVG.outputTranslate(
                                                0,
                                                showText ? 2.79 : -5
                                            )
                                        "
                                        [attr.text-anchor]="
                                            getDisplayParameters(false)
                                                .textAnchor
                                        "
                                        style="font-size:29.482959747314453px;fill:#0092c8;font-family:Roboto-Bold, Roboto;font-weight:700"
                                    >
                                        {{ personScore }}
                                    </svg:text>
                                    @if (showText) {
                                        <svg:text
                                            transform="translate(0 -20.35)"
                                            textLength="48"
                                            lengthAdjust="spacingAndGlyphs"
                                            [attr.text-anchor]="
                                                getDisplayParameters(false)
                                                    .textAnchor
                                            "
                                            style="text-transform: uppercase; font-size:14.23013973236084px;fill:#0092c8;font-family:Roboto-Bold, Roboto;font-weight: 700;letter-spacing:0.07497428354998997em"
                                        >
                                            {{
                                                TR.key(
                                                    'text.common-terms.score'
                                                )
                                            }}
                                        </svg:text>
                                    }
                                </svg:g>
                            </svg:g>
                        }
                    </svg:g>

                    <!-- percentile text at top -->
                    @if (showText) {
                        <svg:g transform="translate(0, 4)">
                            <svg:text
                                [attr.x]="getXFromPercentile(1)"
                                y="0"
                                [ngStyle]="percentileTextStyle"
                            >
                                1st
                            </svg:text>
                            <svg:text
                                [attr.x]="getXFromPercentile(50)"
                                y="0"
                                [ngStyle]="percentileTextStyle"
                            >
                                50th
                            </svg:text>
                            <svg:text
                                [attr.x]="getXFromPercentile(99)"
                                y="0"
                                [ngStyle]="percentileTextStyle"
                            >
                                99th
                            </svg:text>
                        </svg:g>
                    }
                    <svg:g transform="translate(0, 10)">
                        <!-- top gray bar -->
                        <svg:rect
                            x="0"
                            y="0"
                            [attr.width]="width"
                            height="4"
                            style="fill:#c9cbcc"
                        />
                        <!-- 3 percentile ticks marks at top -->
                        <svg:line
                            [attr.x1]="getXFromPercentile(25)"
                            [attr.x2]="getXFromPercentile(25)"
                            y1="3"
                            y2="12"
                            [ngStyle]="topAxisTickMarkStyle"
                        />
                        <svg:line
                            [attr.x1]="getXFromPercentile(50)"
                            [attr.x2]="getXFromPercentile(50)"
                            y1="3"
                            y2="12"
                            [ngStyle]="topAxisTickMarkStyle"
                        />
                        <svg:line
                            [attr.x1]="getXFromPercentile(75)"
                            [attr.x2]="getXFromPercentile(75)"
                            y1="3"
                            y2="12"
                            [ngStyle]="topAxisTickMarkStyle"
                        />
                    </svg:g>
                    <!-- bottom blue/orange bar -->
                    <svg:rect
                        x="0"
                        [attr.y]="bottomRectY"
                        [attr.width]="width"
                        height="16.5"
                        [attr.fill]="getRightGradientId()"
                    ></svg:rect>
                    <!-- bottom numbers -->
                    <svg:text
                        [attr.transform]="
                            SVG.outputTranslate(
                                getXFromPercentile(1),
                                bottomRectY + 12
                            )
                        "
                        [ngStyle]="bottomScoreTextStyle"
                    >
                        100
                    </svg:text>
                    <svg:text
                        [attr.transform]="
                            SVG.outputTranslate(
                                getXFromPercentile(99),
                                bottomRectY + 12
                            )
                        "
                        [ngStyle]="bottomScoreTextStyle"
                    >
                        450
                    </svg:text>
                    <!-- row of bottom text including M Score, Copyright, scoreid -->
                    <svg:g
                        [attr.transform]="
                            SVG.outputTranslate(0, bottomRectY + 12)
                        "
                    >
                        @if (showCopyright) {
                            <svg:text
                                [attr.x]="width"
                                text-anchor="end"
                                style="font-size: 12px;fill:#231f20;"
                            >
                                {{ getCopyrightString() }}
                            </svg:text>
                        }
                    </svg:g>
                </svg:g>
            </svg>
        </div>
    `,
    standalone: false
})
export class CognitiveAssessmentImageComponent
    implements OnInit, AfterContentInit, OnDestroy, OnChanges
{
    @Input()
    personScore: number;
    @Input()
    jobScore: number;
    @Input()
    showText = true;
    @Input()
    showCopyright = false;
    @Input()
    draggable = false;
    @Input()
    isJobColor = false;

    @Output()
    readonly scoreUpdated = new EventEmitter<number>();

    get propertiesList(): string[] {
        return ['personScore', 'jobScore', 'showText', 'showCopyright'];
    }

    SVG: SVGUtilities;

    bottomRectY = 206;

    width = 550; // 504
    dragTriangleWidth = 10;
    dragRectWidth = this.dragTriangleWidth * 4;
    dragTrianglePoints = [
        `${this.dragTriangleWidth * -2},0 ${this.dragTriangleWidth * -1},${
            this.dragTriangleWidth * -1
        } ${this.dragTriangleWidth * -1},${this.dragTriangleWidth}`,
        `${this.dragTriangleWidth * 2},0 ${this.dragTriangleWidth},${
            this.dragTriangleWidth * -1
        } ${this.dragTriangleWidth},${this.dragTriangleWidth}`
    ];

    draggableRectStyle = {
        fill: 'transparent',
        cursor: 'col-resize'
    };

    showOnRight = {
        textAnchor: 'start',
        textStart: 19.69,
        lineHeight: 65,
        isLow: false
    };
    showOnLeft = {
        textAnchor: 'end',
        textStart: -19.69,
        lineHeight: 65,
        isLow: false
    };
    showLowOnRight = {
        textAnchor: 'start',
        textStart: 19.69,
        lineHeight: 105,
        isLow: true
    };
    showLowOnLeft = {
        textAnchor: 'end',
        textStart: -19.69,
        lineHeight: 105,
        isLow: true
    };

    curveOffset = 29;

    percentileTextStyle = {
        fill: '#c8caca',
        'font-weight': 100,
        'text-anchor': 'middle'
    };

    bottomScoreTextStyle = {
        'font-size': '12px',
        fill: '#fff',
        'font-family': 'Roboto-Bold, Roboto',
        'font-weight': 700,
        'text-anchor': 'middle'
    };

    topAxisTickMarkStyle = {
        fill: 'none',
        stroke: '#c8caca',
        'stroke-miterlimit': 10,
        'stroke-width': '1.18743134819734px'
    };

    jobPlacement: Placement;
    personPlacement: Placement;

    // Drag state
    dragging = false;
    svg: SVGElement;
    point: SVGPoint;

    removeListenerCallbacks: Function[] = [];

    constructor(
        public TR: TranslationService,
        private util: Util,
        private elRef: ElementRef,
        public dateFormatUtil: DateFormatUtility
    ) {
        this.SVG = util.svg;
    }

    ngOnInit() {
        if (this.draggable) {
            // This is expensive so throttle it so it's not firing off nonstop
            // during the mousemove event
            const figurePlacement = throttle(() => this.figurePlacement(), 100);
            // Store our listeners to make cleaning them up easier
            this.removeListenerCallbacks.push(
                this.util.eventListener.addEventListener(
                    document.body,
                    'mouseup',
                    () => {
                        if (this.dragging) {
                            this.dragging = false;
                        }
                    }
                ),
                this.util.eventListener.addEventListener(
                    document.body,
                    'mousemove',
                    (e) => {
                        if (this.dragging) {
                            e.preventDefault();
                            const percentile = this.getPercentileFromX(
                                this.cursorPoint(e as MouseEvent).x
                            );
                            this.jobScore =
                                CognitiveAssessment.getScoreFromPercentile(
                                    percentile
                                );
                            this.scoreUpdated.emit(this.jobScore);
                            figurePlacement();
                        }
                    }
                )
            );
        }
    }

    ngAfterContentInit() {
        this.svg = this.elRef.nativeElement.querySelector('svg') as SVGElement;
        this.point = (this.svg as any).createSVGPoint();
    }

    ngOnChanges(changes: SimpleChanges) {
        // When the score changes we need to update the placement of the figure.
        // Notably this needs to occur on first load or the figure won't flip
        // if required.
        if (
            ['personScore', 'jobScore'].some((property) => {
                const change = changes[property];
                return change && change.previousValue !== change.currentValue;
            })
        ) {
            this.figurePlacement();
        }
    }

    ngOnDestroy() {
        this.removeListenerCallbacks.forEach((remove) => remove());
        this.removeListenerCallbacks = [];
    }

    // Get point in global SVG space
    private cursorPoint(evt: MouseEvent): SVGPoint {
        this.point.x = evt.clientX;
        this.point.y = evt.clientY;
        return this.point.matrixTransform(
            (this.svg as any).getScreenCTM().inverse()
        );
    }

    dragMousedown(): void {
        this.dragging = true;
    }

    getCopyrightString() {
        return this.util.strings.formatString(
            this.TR.key('text.assess.cognitve.image.copyright'),
            this.dateFormatUtil.transformDate(new Date())
        );
    }

    private getViewbox(): { width: number; height: number } {
        return {
            height: this.getViewboxHeight(),
            width: this.width
        };
    }

    getViewboxHeight(): number {
        return this.showCopyright ? 242.44 : 225;
    }

    getViewboxDimensions() {
        const viewbox = this.getViewbox();
        return `0 0 ${viewbox.width} ${viewbox.height}`;
    }

    getPaddingBottom(): string {
        return this.SVG.getPaddingBottom(this.width, this.getViewboxHeight());
    }

    getXFromPercentile(percentile: number): number {
        return (
            (percentile / 100) * (this.width - this.curveOffset * 2) +
            this.curveOffset
        );
    }

    getPercentileFromX(x: number): number {
        return (
            ((x - this.curveOffset) / (this.width - this.curveOffset * 2)) * 100
        );
    }

    lineLocation(score: number): number {
        const percentile = CognitiveAssessment.getPercentileFromScore(score);
        return this.getXFromPercentile(percentile);
    }

    getMiddleOfScoreLine(): number {
        return (
            this.getDisplayParameters(true).lineHeight +
            (209.29 - this.getDisplayParameters(true).lineHeight) / 2
        );
    }

    jobBackgroundLocation() {
        return this.jobScore !== undefined
            ? this.lineLocation(this.jobScore)
            : this.width;
    }

    getRightGradientId() {
        const gradientName = this.isJobColor
            ? 'linearGradient-cog-job'
            : 'linearGradient-cog-person';
        return this.SVG.getGradientUrl(gradientName);
    }

    getDisplayParameters(isJob: boolean) {
        if (!this.jobPlacement && !this.personPlacement) {
            this.figurePlacement();
        }
        return isJob ? this.jobPlacement : this.personPlacement;
    }

    hasJobScore(): boolean {
        // 0 is a magic number for jobScore
        return isNumber(this.jobScore) && this.jobScore > 0;
    }
    hasPersonScore(): boolean {
        return isNumber(this.personScore);
    }

    figurePlacement() {
        // Defaults - set here to make sure this is called
        this.jobPlacement = this.showOnLeft;
        this.personPlacement = this.showOnRight;

        const leftMostScoreBeforeFlip = 135;
        const leftMostScoreBeforeFlipJob = 160;
        const rightMostScoreBeforeFlip = 340;
        const spaceForTwo = 110;
        const spaceForOne = 65;

        const scoreDiff = this.jobScore - this.personScore;

        const overlap = scoreDiff > 0 && scoreDiff < spaceForTwo;
        const okayToFlipJobRight = this.jobScore <= rightMostScoreBeforeFlip;
        const okayToFlipPersonLeft =
            this.personScore >= leftMostScoreBeforeFlip;
        const personOffRightEdge = this.personScore > rightMostScoreBeforeFlip;
        const jobOffLeftEdge = this.jobScore < leftMostScoreBeforeFlipJob;
        const roomForPersonFlip =
            this.personScore - spaceForOne > this.jobScore;
        const roomForJobFlip = this.jobScore + spaceForOne < this.personScore;

        if (this.hasJobScore() && this.hasPersonScore()) {
            if (overlap && okayToFlipJobRight && okayToFlipPersonLeft) {
                // case #1 - they would overlap and flipping both fine
                this.jobPlacement = this.showOnRight;
                this.personPlacement = this.showOnLeft;
                return;
            }
            if (overlap && !okayToFlipPersonLeft) {
                // case #2 - they would overlap and flipping left not okay
                // case #2.b flipping job right won't fix
                if (this.jobScore - this.personScore < spaceForOne) {
                    this.jobPlacement = this.showLowOnRight;
                } else {
                    // case #2.a flippingjob right will fix
                    this.jobPlacement = this.showOnRight;
                }
                return;
            }
            if (overlap && !okayToFlipJobRight) {
                // case #3 - they would overlap and flipping right not okay
                // case #2.b flipping person left won't fix
                if (this.jobScore - this.personScore < spaceForOne) {
                    this.personPlacement = this.showLowOnLeft;
                } else {
                    // case #2.a flipping person left will fix
                    this.personPlacement = this.showOnLeft;
                }
                return;
            }
            if (personOffRightEdge && jobOffLeftEdge) {
                // case #4 - both off edges - but can flip
                // Has to be before #5 and #6
                this.jobPlacement = this.showOnRight;
                this.personPlacement = this.showOnLeft;
                return;
            }
            if (personOffRightEdge && roomForPersonFlip) {
                // case #4 - one goes off right edge, but can flip
                this.personPlacement = this.showOnLeft;
                return;
            }
            if (jobOffLeftEdge && roomForJobFlip) {
                // case #5 - one goes off left edge, but can flip
                this.jobPlacement = this.showOnRight;
                return;
            }
            if (personOffRightEdge && !roomForPersonFlip) {
                // case #6 - one goes off right edge, but flipping would overlap
                this.personPlacement = this.showOnLeft;
                this.jobPlacement = this.showLowOnLeft;
                return;
            }
            if (jobOffLeftEdge && !roomForJobFlip) {
                // case #7 - one goes off left edge, but flipping would overlap
                this.jobPlacement = this.showOnRight;
                this.personPlacement = this.showLowOnRight;
                return;
            }
        } else if (this.hasJobScore() && jobOffLeftEdge) {
            this.jobPlacement = this.showOnRight;
        } else if (this.hasPersonScore() && personOffRightEdge) {
            this.personPlacement = this.showOnLeft;
        }
    }
}
